import axios from "axios";
import {
  Button,
  DataGrid,
  DateBox,
  Popover,
} from "devextreme-react";
import {
  Column,
  Editing,
  FilterRow,
  Item,
  Lookup,
  Pager,
  SearchPanel,
  StateStoring,
  Toolbar,
} from "devextreme-react/data-grid";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { divisionOrder, editStatusInfo } from "../data";
import usePopupState from "../hooks/usePopupState";
import { toastMsg } from "../utils";
import { HeaderFilter } from "devextreme-react/gantt";
import CustomStore from "devextreme/data/custom_store";
import IssuesSummaryPopup from "./IssuesSummaryPopup";
import { confirm } from "devextreme/ui/dialog";
import { hasPermission } from "../../../utils/hasPermission";
import { PERM } from "../../../utils/permissions";
import { getReportDivision } from "../access";

const ProjectListWrapper = styled.div`
  .dx-datebox-calendar .dx-dropdowneditor-icon::before {
    content: "\f026";
  }
  .dx-button {
    margin: 0 5px;
    float: left;
  }
  #infoButton {
    i {
      font-size: inherit;
    }
  }
  .edit-status-col {
    padding: 0 !important;
  }
  #projectListGrid {
    max-width: 99vw;
  }
`;

const StatusBtn = styled.button`
  background-color: ${(props) =>
    props.code ? editStatusInfo[props.code].color : "white"};
  border: 0;
  border-radius: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.24);
  font-size: 10px;
  height: 22px;
  margin: 0 1px;
  padding: 0;
  text-align: center;
  width: 22px;
  :disabled {
    box-shadow: none;
    background-color: rgba(0, 0, 0, 0.1);
    cursor: default;
    opacity: 0.38;
  }
`;

const InfoWrapper = styled.div`
  padding: 0 16px;
  > h3 {
    margin: 0;
    &:not(:first-child) {
      margin-top: 16px;
    }
  }
  > li {
    margin: 0;
    list-style: none;
    margin-top: 0.5em;
    padding-left: 1.5em;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    > div {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      margin-right: 24px;
      box-shadow: 0px 0px 0px 1px gray;
    }
  }
  > div:last-child {
    display: grid;
    grid-template-columns: 60px auto;
    grid-gap: 0.5em;
    margin-top: 0.5em;
    > div:nth-child(odd) {
      font-weight: bold;
      text-align: center;
    }
  }
`;

const ProjectList = ({
  handleClickMenu,
  projectInfo,
  projectListRef,
  setProjectInfo,
  setYearMonth,
  yearMonth,
  fullScreen,
  setFullScreen,
}) => {
  const [priorityDataSource, setPriorityDataSource] = useState(null);
  const [projectDataSource, setProjectDataSource] = useState(null);
  const [showMyProject, setShowMyProject] = useState(false);
  const userInfo = useSelector((state) => state.userInfo);
  const reportDiv = getReportDivision(userInfo.div);
  const { mode, setPopupClose, setPopupOpen } = usePopupState();

  const canEdit = priorityDataSource !== null && hasPermission(userInfo.permissions, PERM.REPORT_PROJECT_EDIT);
  const canComplete = hasPermission(userInfo.permissions, PERM.REPORT_PROJECT_COMPLETE);

  const prioCodeListIdRef = useRef(null);

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_BECCO_API_URL}/api/common/codeList?name=Priority&cat=10`
      )
      .then((res) => {
        prioCodeListIdRef.current = res.data.codeListId;

        setPriorityDataSource(res.data.code);
      })
      .catch(() => toastMsg("우선순위 목록을 불러오지 못했습니다.", false));
  }, []);

  useEffect(() => {
    if (yearMonth && userInfo.employeeNo) {
      setProjectDataSource(
        new CustomStore({
          key: "contrNo",
          load: async () => {
            //! api R6. Get the project list
            const response = await axios.get(
              `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/${yearMonth}?employeeNo=${userInfo.employeeNo}`
            );

            return response.data;
          },
          // update: async (div, employeeNo) => {
          update: async (key, values) => {
            //! api U2. Update the project status
            if (key === "updateAll") {
              const response = await axios.put(
                `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/editStatus/${yearMonth}`,
                // { div, employeeNo }
                { div: values.div, employeeNo: values.employeeNo }
              );
              if (!response) throw new Error("Monthly report completion failed");
              toastMsg(
                `${values.div}부서의 Edit Status가 전체완료로 변경되었습니다.`,
                true
              );
              return response.data;
            } else {
              if (prioCodeListIdRef.current == null) {
                throw new Error("Priority list is not loaded");
              }
              //! api U7. Update the project priority
              await axios.put(
                `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/prio/${key}`,
                {
                  prioCodeListId: prioCodeListIdRef.current,
                  prioCodeId: values.prioCodeId,
                }
              );
            }
          },
          errorHandler: (error) => {
            toastMsg(`에러가 발생했습니다. 관리자에게 문의해주세요.`, false);
            console.log(error);
          },
        })
      );
    }
  }, [yearMonth, userInfo.employeeNo]);

  const handleCellStyle = (e) => {
    // datagrid의 header styling.
    if (e.rowType === "header") {
      e.cellElement.style.fontWeight = "bold";
      e.cellElement.style.textAlign = "center";
      e.cellElement.style.backgroundColor = "#E2EFDA";
    }
    // "Priority" column의 data cell styling. TODO
    if (e.rowType === "data" && e.column.caption === "Priority") {
      const name = e.data.prioCodeName;
      const color = e.data.color;
      if (name !== "null" && color !== "null") {
        // e.cellElement.textContent = name;
        e.cellElement.style.backgroundColor = color;
      } else {
        // e.cellElement.textContent = "";
      }
    }
    // 현재 선택된 프로젝트의 row styling.
    if (
      e.rowType === "data" &&
      e.data.contrNo === projectInfo.contrNo &&
      e.column.caption !== "Priority"
    ) {
      e.cellElement.style.backgroundColor = "#E6E6E6";
    }
  };

  const handleChangeYearMonth = (e) => {
    const dateRegex = /^\d{4}\/\d{2}\/\d{2}$/;
    const yearMonthRegex = /^\d{4}-\d{2}$/;

    setProjectInfo({ ...projectInfo, contrNo: null });
    if (dateRegex.test(e.value)) {
      const [year, month] = e.value.split("/"); // 날짜를 연도와 월로 분리
      setYearMonth(`${year}-${month}`); // "yyyy-MM" 형식으로 반환
    } else if (yearMonthRegex.test(e.value)) {
      setYearMonth(e.value); // 이미 "yyyy-MM" 형식이라면 그대로 반환
    }
  };

  const handleClickRow = (e) => {
    console.log(e.data.contrNo);
    setProjectInfo({
      ...projectInfo,
      contrNo: e.data.contrNo,
      isMyProject: e.data.myProject === "Y",
      isEnrollmentStatusUsed: e.data.enrollStatus === "Y",
    });
  };

  const handleClickSumBtn = () => {
    handleClickMenu("summary", false);
  };

  const handleEditStatusAtOnce = async () => {
    const result = await confirm(
      `${reportDiv} 부서에서 진행하는 프로젝트의 Edit Status가 모두 "완료"로 변경됩니다.<br/>변경하시겠습니까?`,
      "Update All"
    );

    if (result) {
      projectDataSource
        .update("updateAll", {
          div: reportDiv,
          employeeNo: userInfo.employeeNo,
        })
        .then(() => projectListRef.current.instance.refresh())
        .catch(() => {}); // CustomStore의 errorHandler에서 안내하고 현재 화면을 유지한다.
    }
  };

  const handleFilterMyProject = (e) => {
    const isChecked = e.value;
    if (isChecked) {
      projectListRef.current.instance.filter(["myProject", "=", "Y"]);
      setShowMyProject(true);
    } else {
      projectListRef.current.instance.filter([
        ["myProject", "=", "Y"],
        "or",
        ["myProject", "=", "N"],
      ]);
      setShowMyProject(false);
    }
  };

  const renderEditStatus = (e) => {
    const data = e.data;
    return (
      <div>
        {divisionOrder.map((div) => {
          return (
            <StatusBtn
              key={`${div}Btn`}
              code={data[div]}
              disabled={data[div] === "0"}
            >
              {div}
            </StatusBtn>
          );
        })}
      </div>
    );
  };

  const onMinMaxIconClick = () => {
    setFullScreen(!fullScreen);
  };

  const calculateDisplayValue = (data) => {
    if (data.issues > 0) {
      return data.nonClosedIssues + " / " + data.issues;
    } else {
      return data.issues;
    }
  };

  const onChangesChange = (changes) => {
    if (changes.length > 0) {
      projectDataSource.update(changes[0].key, changes[0].data)
        .then(() => projectListRef.current.instance.refresh())
        .catch(() => {}); // CustomStore의 errorHandler에서 실패를 안내한다.
    }
  };

  return (
    <ProjectListWrapper>
      <DateBox
        calendarOptions={{
          maxZoomLevel: "year",
          minZoomLevel: "decade",
          zoomLevel: "year",
        }}
        displayFormat={"yyyy년 M월"}
        id="yearMonthDateBox"
        invalidDateMessage={"YYYY년 M월 포맷이어야 합니다"}
        onValueChanged={handleChangeYearMonth}
        pickerType="calendar"
        type="date"
        value={yearMonth}
        width={200}
      />
      <div style={{ height: "16px" }} />
      <DataGrid
        allowColumnResizing={true}
        columnResizingMode={"nextColumn"}
        dataSource={projectDataSource}
        height={fullScreen ? "auto" : 500}
        id="projectListGrid"
        onCellPrepared={handleCellStyle}
        onRowClick={handleClickRow}
        ref={projectListRef}
        showBorders={true}
        showColumnLines={true}
        width={"100%"}
        wordWrapEnabled={true}
      >
        <Editing
          // refreshMode="reshape"
          mode={"cell"}
          allowUpdating={true}
          // selectTextOnEditStart={true}
          // startEditAction={"click"}
          onChangesChange={onChangesChange}
        />
        <FilterRow visible={true} />
        <HeaderFilter allowSearch={true} visible={true} />
        <Pager
          allowedPageSizes={[5, 10, 20, 30, "all"]}
          displayMode="full"
          showPageSizeSelector={true}
          showInfo={true}
          showNavigationButtons={true}
          visible={true}
        />
        <SearchPanel placeholder="Search..." visible={true} width={200} />
        <StateStoring
          enabled={true}
          savingTimeout={1000}
          storageKey="MonthlyReport.ProjectList"
          type="localStorage"
        />
        <Toolbar>
          <Item
            location="before"
            name="myProjectCheckBox"
            options={{
              value: showMyProject,
              onValueChanged: handleFilterMyProject,
              text: "My Project",
            }}
            widget="dxCheckBox"
          />
          <Item
            location="after"
            name="turnEditStatusToCompleteButton"
            options={{
              disabled: !canComplete,
              onClick: handleEditStatusAtOnce,
              stylingMode: "contained",
              text: "Update All",
              type: "success",
            }}
            widget="dxButton"
          />
          <Item
            location="after"
            name="totalIssuesPopup"
            options={{
              onClick: setPopupOpen,
              stylingMode: "contained",
              text: "ISSUES",
              type: "success",
            }}
            widget="dxButton"
          />
          <Item
            location="after"
            name="navSummaryButton"
            options={{
              onClick: handleClickSumBtn,
              stylingMode: "contained",
              text: "SUMMARY",
              type: "success",
            }}
            widget="dxButton"
          />
          <Item
            location="after"
            name="infoButton"
            options={{
              elementAttr: { id: "infoButton" },
              icon: "fa-solid fa-info",
              stylingMode: "outlined",
              type: "normal",
            }}
            widget="dxButton"
          />
          <Item location={"after"}>
            <Button
              onClick={onMinMaxIconClick}
              icon={
                fullScreen ? `fa-solid fa-minimize` : `fa-solid fa-maximize`
              }
            />
          </Item>
          <Item name="searchPanel" />
        </Toolbar>
        <Column
          dataField="editStatus"
          alignment="center"
          allowFiltering={false}
          allowHeaderFiltering={false}
          caption="Edit Status"
          cellRender={renderEditStatus}
          cssClass="edit-status-col"
          width={200}
          allowEditing={false}
        />
        <Column
          dataField="prioCodeId"
          alignment="center"
          caption="Priority"
          minWidth={110}
          allowEditing={canEdit}
        >
          <Lookup
            dataSource={priorityDataSource}
            displayExpr="name"
            valueExpr="codeId"
          />
          <HeaderFilter
            dataSource={priorityDataSource?.map(({ codeId, name }) => ({
              text: name,
              value: codeId,
            }))}
            allowSearch={true}
          />
        </Column>
        <Column
          dataField="contrNo"
          alignment="center"
          caption="Contract No."
          width={140}
          allowEditing={false}
        />
        <Column
          dataField="issues"
          alignment="center"
          caption="#Issues"
          minWidth={140}
          allowEditing={false}
          calculateDisplayValue={calculateDisplayValue}
        />
        <Column
          dataField="sprName"
          alignment="left"
          caption="Sponsor Name"
          width={180}
          allowEditing={false}
        />
        <Column
          dataField="projectName"
          alignment="left"
          caption="Project Name"
          width={"100%"}
          allowEditing={false}
        />
        <Column
          dataField="type"
          alignment="left"
          caption="Type"
          width={160}
          allowEditing={false}
        />
        <Column
          dataField="ind"
          alignment="left"
          caption="Indication"
          width={180}
          allowEditing={false}
        />
      </DataGrid>
      <Popover
        hideEvent={"mouseleave"}
        showEvent={"mouseenter"}
        target={"#infoButton"}
      >
        <InfoWrapper>
          <h3>Edit Status</h3>
          {editStatusInfo.map((el, idx) => {
            return (
              <li key={`status${idx}`}>
                <div style={{ backgroundColor: el.color }} />
                <span>{el.text}</span>
              </li>
            );
          })}
          <h3>#Issues</h3>
          <li>
            <span>#Non Closed Issues / #Total Issues</span>
          </li>
          <h3>Priority</h3>
          <div>
            {priorityDataSource?.map((el, idx) => {
              return (
                <>
                  <div key={`name${idx}`}>{el.name}</div>
                  <div key={`descr${idx}`}>{el.descr}</div>
                </>
              );
            })}
          </div>
        </InfoWrapper>
      </Popover>
      {mode === "view" && (
        <IssuesSummaryPopup
          setPopupClose={setPopupClose}
          yearMonth={yearMonth}
        />
      )}
    </ProjectListWrapper>
  );
};

export default ProjectList;
